import { Injectable, signal } from '@angular/core';

/**
 * Item indexé par la palette de recherche globale.
 * `icon` est soit un nom Material Icon (`'search'`, `'home'`...) soit un fragment SVG
 * (chaîne commençant par `<path>` à interpréter via [innerHTML] dans un <svg> parent).
 */
export type SearchItem = {
    title: string;
    subtitle: string;
    url: string;
    icon: string;
};

/**
 * Registre centralisé des items navigables exposés par la palette de recherche (Ctrl+K).
 *
 * Les modules peuvent enregistrer dynamiquement leurs entrées (ex. au bootstrap de
 * leurs routes) plutôt que d'avoir la liste hardcodée dans le composant.
 *
 * Usage :
 * - lecture par le composant SearchPaletteComponent via `items()` quand aucun
 *   `[items]` n'est passé en input,
 * - écriture par n'importe quel module via `setItems()` (remplace tout) ou
 *   `register()` (ajoute / met à jour par url).
 */
@Injectable({ providedIn: 'root' })
export class SearchPaletteRegistryService {
    private readonly _items = signal<SearchItem[]>(DEFAULT_SEARCH_ITEMS);

    /** Signal en lecture seule des items courants. */
    readonly items = this._items.asReadonly();

    /** Remplace intégralement la liste des items. */
    setItems(items: SearchItem[]): void {
        this._items.set([...items]);
    }

    /** Ajoute ou met à jour un item identifié par son `url`. */
    register(item: SearchItem): void {
        this._items.update((list) => {
            const idx = list.findIndex((x) => x.url === item.url);
            if (idx >= 0) {
                const next = [...list];
                next[idx] = item;
                return next;
            }
            return [...list, item];
        });
    }

    /** Supprime l'item identifié par son `url`. */
    unregister(url: string): void {
        this._items.update((list) => list.filter((x) => x.url !== url));
    }

    /** Réinitialise à la liste par défaut. */
    reset(): void {
        this._items.set(DEFAULT_SEARCH_ITEMS);
    }
}

/**
 * Items par défaut pour préserver la compatibilité avec le précédent comportement
 * (palette utilisable sans config dans app.html / showcase).
 */
const DEFAULT_SEARCH_ITEMS: SearchItem[] = [
    {
        title: 'Dashboard',
        subtitle: 'Tableau de bord principal',
        url: '/dashboard',
        icon:
            '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 ' +
            '2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>',
    },
    {
        title: 'Utilisateurs',
        subtitle: 'Gestion des utilisateurs',
        url: '/administration/utilisateurs',
        icon:
            '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 ' +
            '0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"/>',
    },
    {
        title: 'Rôles',
        subtitle: 'Gestion des rôles RBAC',
        url: '/administration/rbac/roles',
        icon:
            '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 ' +
            '0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 ' +
            '3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>',
    },
    {
        title: 'Permissions',
        subtitle: 'Gestion des permissions',
        url: '/administration/rbac/permissions',
        icon:
            '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 ' +
            '1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"/>',
    },
    {
        title: 'Audit',
        subtitle: "Journaux d'audit",
        url: '/administration/audits',
        icon:
            '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 ' +
            '00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"/>',
    },
    {
        title: 'Sessions',
        subtitle: 'Gestion des sessions actives',
        url: '/administration/configuration/sessions',
        icon:
            '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 ' +
            '002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>',
    },
    {
        title: 'Profil',
        subtitle: 'Mon profil utilisateur',
        url: '/profile',
        icon:
            '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 ' +
            '00-7-7z"/>',
    },
];
